import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';

import { Message } from '../../conversations/types/Conversation';

interface UseWebWidgetMessageRailScrollOptions {
  conversationId: string | null | undefined;
  messages: Message[];
  typingIndicatorCount?: number;
}

interface UseWebWidgetMessageRailScrollResult {
  messageRailRef: RefObject<HTMLDivElement | null>;
  scrollToBottom: () => void;
}

export const useWebWidgetMessageRailScroll = ({
  conversationId,
  messages,
  typingIndicatorCount = 0,
}: UseWebWidgetMessageRailScrollOptions): UseWebWidgetMessageRailScrollResult => {
  const messageRailRef = useRef<HTMLDivElement | null>(null);
  const lastReadMessageCount = useRef(0);
  const lastFirstMessageId = useRef<string | null>(null);
  const lastLastMessageId = useRef<string | null>(null);
  const initialScrollConversationId = useRef<string | null>(null);

  const messageCount = messages.length;
  const firstMessageId = messages[0]?.id ?? null;
  const lastMessageId = messageCount > 0 ? messages[messageCount - 1].id : null;

  useEffect(() => {
    lastReadMessageCount.current = 0;
    lastFirstMessageId.current = null;
    lastLastMessageId.current = null;
    initialScrollConversationId.current = null;
  }, [conversationId]);

  const scrollToBottom = useCallback(() => {
    const rail = messageRailRef.current;
    if (!rail) {
      return;
    }

    rail.scrollTop = rail.scrollHeight;
    lastReadMessageCount.current = messageCount;
    lastFirstMessageId.current = firstMessageId;
    lastLastMessageId.current = lastMessageId;
  }, [firstMessageId, lastMessageId, messageCount]);

  useEffect(() => {
    if (!messageRailRef.current) {
      return;
    }

    const newMessageCount = messageCount;
    const nextFirstMessageId = firstMessageId;
    const nextLastMessageId = lastMessageId;

    const olderMessagesPrepended = newMessageCount > lastReadMessageCount.current
      && lastReadMessageCount.current > 0
      && nextFirstMessageId !== lastFirstMessageId.current
      && nextLastMessageId === lastLastMessageId.current;

    if (olderMessagesPrepended) {
      lastReadMessageCount.current = newMessageCount;
      lastFirstMessageId.current = nextFirstMessageId;
      lastLastMessageId.current = nextLastMessageId;
      return;
    }

    const messageAppendedAtEnd = Boolean(nextLastMessageId)
      && nextLastMessageId !== lastLastMessageId.current;
    const messageCountIncreased = newMessageCount > lastReadMessageCount.current;

    if (messageAppendedAtEnd || messageCountIncreased) {
      const timer = window.setTimeout(() => {
        if (messageRailRef.current) {
          messageRailRef.current.scrollTop = messageRailRef.current.scrollHeight;
        }
      }, 100);

      lastReadMessageCount.current = newMessageCount;
      lastFirstMessageId.current = nextFirstMessageId;
      lastLastMessageId.current = nextLastMessageId;

      return () => window.clearTimeout(timer);
    }

    lastFirstMessageId.current = nextFirstMessageId;
    lastLastMessageId.current = nextLastMessageId;
  }, [firstMessageId, lastMessageId, messageCount]);

  useEffect(() => {
    if (typingIndicatorCount <= 0 || !messageRailRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      if (messageRailRef.current) {
        messageRailRef.current.scrollTop = messageRailRef.current.scrollHeight;
      }
    }, 50);

    return () => window.clearTimeout(timer);
  }, [typingIndicatorCount]);

  useEffect(() => {
    if (!conversationId || !messageRailRef.current || initialScrollConversationId.current === conversationId) {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollToBottom();
      initialScrollConversationId.current = conversationId;
    }, 50);

    return () => window.clearTimeout(timer);
  }, [conversationId, messageCount, scrollToBottom]);

  return {
    messageRailRef,
    scrollToBottom,
  };
};
